import * as React from 'react';
import { Badge, Box, Button, Checkbox, Flex, Spinner, Text, useToast } from '@chakra-ui/react';
import { CheckCircle, Lightning } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import { useContaCilLancamentos } from '../../../../../../hooks/useContaCilLancamentos';
import { useContaCilConfirmarSugeridos } from '../../../../../../hooks/useContaCilSchoolAi';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { fmtData, fmtValor } from '../../conciliar/shared';

const C = {
  violet: '#8B5CF6',
  amber: '#F59E0B',
  green: '#10B981',
  red: '#EF4444',
  ink: '#0F172A',
  muted: '#64748B',
  subtle: '#94A3B8',
};

// Rótulos legíveis das fontes SUGERE (o detector que pré-preencheu).
const FONTE_LABEL: Record<string, string> = {
  'fornecedor-sugere': 'Fornecedor',
  'cliente-sugere': 'Cliente',
  'empregado-sugere': 'Folha',
  'pix-default-sugere': 'Pix padrão',
  'ancora-sugere': 'Âncora',
  'aplicacao-sugere': 'Aplicação',
  'memoria-sugere': 'Memória',
  'socio-sugere': 'Sócio',
};
const fonteLabel = (f?: string | null) => (f ? (FONTE_LABEL[f] ?? f) : '—');

/**
 * Fila SUGERE (F4.5): os pendentes que o motor já pré-preencheu (as 2 pernas)
 * aguardando o "ok" do contador. Confirmar (1-clique/lote) vira conciliado e
 * cada confirmação alimenta a memória L2 — o multiplicador humano da refinaria.
 */
export function FilaSugeridosTab({ empresaId }: { empresaId: number }) {
  const toast = useToast();
  const lista = useContaCilLancamentos({
    empresaId,
    status: 'pendente',
    page: 1,
    size: 200,
    ordem: 'antigos',
  });
  const confirmar = useContaCilConfirmarSugeridos();
  const [sel, setSel] = React.useState<Set<number>>(new Set());

  // A fila são os pendentes JÁ pré-preenchidos (débito E crédito) = SUGERE
  // aguardando confirmação. O backend não tem filtro "com contrapartida",
  // então recortamos aqui sobre a página de pendentes.
  const fila = React.useMemo(
    () => (lista.data?.items ?? []).filter(l => l.debito_poc && l.credito_poc),
    [lista.data],
  );

  // Some da seleção o que saiu da fila (após confirmar + refetch).
  React.useEffect(() => {
    setSel(prev => {
      const ids = new Set(fila.map(l => l.id));
      const next = new Set(Array.from(prev).filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [fila]);

  const allSel = fila.length > 0 && sel.size === fila.length;
  const toggle = (id: number) =>
    setSel(p => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () => setSel(allSel ? new Set() : new Set(fila.map(l => l.id)));

  const onConfirm = async () => {
    if (sel.size === 0) return;
    try {
      const r = await confirmar.mutateAsync({ empresa_id: empresaId, ids: Array.from(sel) });
      toast({
        title: `${r.confirmados} confirmado(s)`,
        description: `${r.memorias_gravadas} viraram memória${
          r.ignorados ? ` · ${r.ignorados} ignorado(s)` : ''
        }`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setSel(new Set());
    } catch (e) {
      toast({
        title: 'Falha ao confirmar',
        description: extractApiErrorMessage(e),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (lista.isLoading) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Flex align="center" justify="center" gap={3}>
          <Spinner size="sm" color={C.violet} />
          <Text fontSize="sm" color={C.muted}>
            Carregando a fila de sugestões…
          </Text>
        </Flex>
      </GlassCard>
    );
  }

  if (lista.isError) {
    return (
      <GlassCard intensity="medium" p={8}>
        <Text fontSize="sm" color={C.ink} fontWeight="600" textAlign="center">
          Não foi possível carregar a fila. Recarregue a página.
        </Text>
      </GlassCard>
    );
  }

  if (fila.length === 0) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Flex direction="column" align="center" gap={2}>
          <CheckCircle size={28} weight="fill" color={C.green} />
          <Text fontSize="sm" color={C.ink} fontWeight="600">
            Nenhuma sugestão na fila
          </Text>
          <Text fontSize="12px" color={C.muted} textAlign="center">
            O motor não tem pendências pré-preenchidas aguardando confirmação para esta empresa.
          </Text>
        </Flex>
      </GlassCard>
    );
  }

  return (
    <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
      <Flex align="center" justify="space-between" mb={4} gap={3} wrap="wrap">
        <Box>
          <Flex align="center" gap={2}>
            <Lightning size={18} weight="fill" color={C.amber} />
            <Text fontSize="md" fontWeight="700" color={C.ink}>
              Fila de sugestões
            </Text>
            <Badge borderRadius="full" colorScheme="orange" fontSize="10px">
              {fila.length}
            </Badge>
          </Flex>
          <Text fontSize="12px" color={C.muted} mt={1}>
            Pré-preenchidas pelo motor. Confirmar vira conciliado e ensina a memória.
          </Text>
        </Box>
        <Button
          size="sm"
          leftIcon={<CheckCircle size={16} weight="fill" />}
          bg={C.green}
          color="white"
          _hover={{ bg: '#0E9F6E' }}
          borderRadius="full"
          isDisabled={sel.size === 0}
          isLoading={confirmar.isPending}
          onClick={onConfirm}
        >
          Confirmar {sel.size > 0 ? sel.size : ''} selecionada{sel.size === 1 ? '' : 's'}
        </Button>
      </Flex>

      <Flex
        align="center"
        gap={3}
        px={3}
        py={2}
        mb={1}
        borderBottomWidth="1px"
        borderColor="rgba(15,23,42,0.06)"
      >
        <Checkbox isChecked={allSel} onChange={toggleAll} colorScheme="green" />
        <Text fontSize="11px" color={C.subtle} fontWeight="600">
          {allSel ? 'Desmarcar todas' : 'Selecionar todas'}
        </Text>
      </Flex>

      <Box>
        {fila.map(l => {
          const contra = l.dc === 'D' ? l.debito_poc : l.credito_poc;
          return (
            <Flex
              key={l.id}
              align="center"
              gap={3}
              px={3}
              py={2}
              borderRadius="lg"
              _hover={{ bg: 'rgba(15,23,42,0.03)' }}
              cursor="pointer"
              onClick={() => toggle(l.id)}
            >
              <Checkbox
                isChecked={sel.has(l.id)}
                onChange={() => toggle(l.id)}
                colorScheme="green"
                onClick={e => e.stopPropagation()}
              />
              <Text fontSize="11px" color={C.subtle} w="78px" flexShrink={0}>
                {fmtData(l.data)}
              </Text>
              <Box flex="1" minW={0}>
                <Text fontSize="13px" color={C.ink} noOfLines={1}>
                  {l.historico}
                </Text>
                {l.contraparte ? (
                  <Text fontSize="11px" color={C.muted} noOfLines={1}>
                    {l.contraparte}
                  </Text>
                ) : null}
              </Box>
              <Badge
                borderRadius="full"
                fontSize="9px"
                colorScheme={l.fonte_match === 'memoria-sugere' ? 'purple' : 'blue'}
                flexShrink={0}
              >
                {fonteLabel(l.fonte_match)}
              </Badge>
              <Text
                fontSize="11px"
                color={C.muted}
                w="64px"
                textAlign="right"
                flexShrink={0}
                title="conta-contrapartida sugerida"
              >
                → {contra}
              </Text>
              <Text
                fontSize="12px"
                fontWeight="600"
                color={l.dc === 'C' ? C.green : C.ink}
                w="92px"
                textAlign="right"
                flexShrink={0}
              >
                {l.dc === 'C' ? '+' : '-'}
                {fmtValor(l.valor)}
              </Text>
            </Flex>
          );
        })}
      </Box>

      {(lista.data?.total ?? 0) > 200 ? (
        <Text fontSize="11px" color={C.subtle} mt={3} textAlign="center">
          Há mais pendências além desta página. Confirme em lotes — a lista recarrega sozinha.
        </Text>
      ) : null}
    </GlassCard>
  );
}
